import React, { useMemo, useState } from 'react'
import { AlertTriangle, Star } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/Badge'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import useGetVaults from '@/services/api/vault/useGetVaults'
import {
  type XeroAccount,
  useSaveVaultMapping,
} from '@/services/api/integrations/useXeroMapping'
import { useAccountingProviderLabel } from '@/services/api/integrations/useAccountingProviderLabel'

interface VaultOption {
  id: number
  name: string
}

export function useVaultOptions(): VaultOption[] {
  const { data } = useGetVaults()
  return useMemo(
    () =>
      (data ?? [])
        .flatMap((item: any) => (item.is_group ? item.vaults ?? [] : [item]))
        .map((vault: any) => ({ id: vault.id, name: vault.name })),
    [data],
  )
}

export function accountLabel(account: Pick<XeroAccount, 'name' | 'code'>) {
  return account.code ? `${account.code} · ${account.name}` : account.name
}

/** One imported bank account and the vault(s) its transactions land in. */
export default function VaultMappingRow({
  account,
  canEdit,
  rawTransactionId,
  onSaved,
  startEditing = false,
}: {
  account: XeroAccount
  canEdit: boolean
  /** Set when mapping inline from the Bank Feed: that line is re-processed. */
  rawTransactionId?: number
  onSaved?: () => void
  startEditing?: boolean
}) {
  const vaults = useVaultOptions()
  const save = useSaveVaultMapping()
  const provider = useAccountingProviderLabel()
  const mapped = account.vault_mappings
  const [editing, setEditing] = useState(startEditing)
  const [selected, setSelected] = useState<number[]>(() =>
    mapped.map((m) => m.vault_id),
  )
  const [defaultId, setDefaultId] = useState<number | null>(
    () => mapped.find((m) => m.is_default)?.vault_id ?? null,
  )

  const startEdit = () => {
    setSelected(mapped.map((m) => m.vault_id))
    setDefaultId(mapped.find((m) => m.is_default)?.vault_id ?? null)
    setEditing(true)
  }
  const toggle = (vaultId: number, checked: boolean) => {
    const next = checked
      ? [...selected, vaultId]
      : selected.filter((id) => id !== vaultId)
    setSelected(next)
    if (next.length === 1) setDefaultId(next[0])
    else if (!next.includes(defaultId ?? -1)) setDefaultId(null)
  }

  const needsDefault =
    selected.length > 1 && !selected.includes(defaultId ?? -1)
  const handleSave = async () => {
    await save.mutateAsync({
      accountId: account.id,
      vaultIds: selected,
      defaultVaultId: selected.length === 1 ? selected[0] : defaultId,
      rawTransactionId,
    })
    setEditing(false)
    onSaved?.()
  }

  return (
    <div className="space-y-3 border-b py-3 last:border-b-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">{accountLabel(account)}</p>
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {account.currency_code && <span>{account.currency_code}</span>}
            {!account.is_active && (
              <Badge variant="outline">Archived in {provider}</Badge>
            )}
          </div>
        </div>
        {!editing && (
          <div className="flex flex-wrap items-center gap-2">
            {mapped.length === 0 ? (
              <span className="flex items-center gap-1 text-sm text-amber-700">
                <AlertTriangle className="size-4" /> Not mapped to a vault
              </span>
            ) : (
              mapped.map((m) => (
                <Badge
                  key={m.vault_id}
                  variant={m.is_default ? 'default' : 'outline'}
                >
                  {m.is_default && mapped.length > 1 && (
                    <Star className="fill-current" />
                  )}
                  {m.vault?.name ?? `Vault #${m.vault_id}`}
                </Badge>
              ))
            )}
            {canEdit && (
              <Button variant="outline" size="sm" onClick={startEdit}>
                {mapped.length ? 'Change' : 'Map'}
              </Button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className="space-y-3 rounded-md border bg-layout-secondary p-3">
          <p className="text-xs text-muted-foreground">
            Pick the vault(s) this bank account feeds. With several, the default
            vault is used when a transaction can't be matched to a specific
            vault by its category.
          </p>
          <div className="grid max-h-64 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2">
            {vaults.map((vault) => {
              const checked = selected.includes(vault.id)
              return (
                <div
                  key={vault.id}
                  className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-white"
                >
                  <label className="flex min-w-0 cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(value) =>
                        toggle(vault.id, value === true)
                      }
                    />
                    <span className="truncate">{vault.name}</span>
                  </label>
                  {checked && selected.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setDefaultId(vault.id)}
                      className={cn(
                        'flex shrink-0 items-center gap-1 text-xs',
                        defaultId === vault.id
                          ? 'font-semibold text-txt-accent'
                          : 'text-muted-foreground hover:text-txt-primary',
                      )}
                      aria-pressed={defaultId === vault.id}
                    >
                      <Star
                        className={cn(
                          'size-3.5',
                          defaultId === vault.id && 'fill-current',
                        )}
                      />
                      Default
                    </button>
                  )}
                </div>
              )
            })}
          </div>
          {needsDefault && (
            <p className="text-xs text-red-700">
              Choose which selected vault is the default.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={selected.length === 0 || needsDefault || save.isPending}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
